// The field page's resend queue (PRD 108 §4.4, on the installed page until
// the native app): a status tap or a clock press made with no signal is KEPT
// on the phone and sent, in order, when the signal comes back — the
// technician never has to remember to press Try again in a basement.
//
// Only what PRD 108's FieldService::OfflineWrites lists is ever queued: the
// visit status tap and the clock (in, out, break start, break end). Every
// entry carries the id and the time taken when the press LANDED; a resend
// uses the same ones, so the server's replay rule (the same id answers the
// stored row with 200 and writes nothing) makes a press that got through
// before the connection dropped count once.
//
//   - ONE QUEUE PER PERSON AND BUSINESS, in localStorage, first in first out,
//     drained one at a time; a business's entries are sent only while it is
//     the selected business (X-Business-Id is the page's).
//   - A FAILURE THAT IS THE NETWORK'S (no answer, 5xx, 429) stops the drain
//     and leaves the entry for the next try: on `online`, when the page comes
//     back to the front, and every 30 seconds while anything waits.
//   - A REFUSAL (409, 422, 403 — "the office had already finished this
//     visit") drops the entry into *Not recorded*, in the server's words with
//     the time it was pressed, for the technician to tell the office. It is
//     never resent as a new press.
//   - Signing out clears it with the snapshots: a shared phone keeps nothing
//     of the person who left.
//   - BEING signed out (a refresh the API refused, the session's time running
//     out) keeps every queue that still has presses to send (PRD 108 §4.2:
//     "sent to sign in with the queue kept"). The queue is keyed by the
//     person, so it waits for that person's next sign-in on this phone and is
//     invisible to anyone else's.

import { BusinessApiError } from '../../../services/businessApiCommon';
import { fieldDayService } from '../../../services/fieldDayService';
import { jobRefusalMessage } from '../../../services/fieldJobService';
import { getGlobalBusinessId } from '../../../utils/apiUtils';
import type { ClockEdgeParams, DayTapParams } from '../../../types/fieldDayApi';

export type QueuedWrite =
  | { kind: 'tap'; id: string; visitId: string; params: DayTapParams; label: string; pressedAt: string }
  | { kind: 'clock_in' | 'clock_out'; id: string; params: ClockEdgeParams; label: string; pressedAt: string }
  | { kind: 'break_start' | 'break_end'; id: string; label: string; pressedAt: string };

export interface NotRecorded {
  id: string;
  label: string;
  pressedAt: string;
  message: string;
}

interface Stored {
  queue: QueuedWrite[];
  notRecorded: NotRecorded[];
}

export interface QueueState extends Stored {
  sending: boolean;
  /** How many entries the server has accepted since the page loaded — screens reload when it moves. */
  sent: number;
}

const PREFIX = 'greenback.fieldQueue.v1';
const USER_KEY = 'user_uuid';
const RETRY_EVERY_MS = 30_000;
const NOT_RECORDED_LIMIT = 20;

type Listener = (state: QueueState) => void;
const listeners = new Set<Listener>();
let sending = false;
let sent = 0;
let timer: ReturnType<typeof setInterval> | null = null;
let started = false;

function storageKey(): string | null {
  let user: string | null = null;
  try {
    user = window.localStorage.getItem(USER_KEY);
  } catch {
    return null;
  }
  const business = getGlobalBusinessId();
  return user && business ? `${PREFIX}:${user}:${business}` : null;
}

function read(): Stored {
  const key = storageKey();
  if (!key) return { queue: [], notRecorded: [] };
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) ?? 'null') as Stored | null;
    return { queue: parsed?.queue ?? [], notRecorded: parsed?.notRecorded ?? [] };
  } catch {
    return { queue: [], notRecorded: [] };
  }
}

function write(stored: Stored): void {
  const key = storageKey();
  if (!key) return;
  try {
    if (stored.queue.length === 0 && stored.notRecorded.length === 0) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(stored));
  } catch {
    // A full or blocked store keeps nothing; the press shows Not sent instead.
  }
  announce();
}

export function queueState(): QueueState {
  return { ...read(), sending, sent };
}

function announce(): void {
  const state = queueState();
  listeners.forEach(listener => listener(state));
}

export function onQueueChange(listener: Listener): () => void {
  listeners.add(listener);
  listener(queueState());
  return () => {
    listeners.delete(listener);
  };
}

/** A failure the network caused, as opposed to an answer from the server. */
export function isRetryable(error: unknown): boolean {
  if (!(error instanceof BusinessApiError)) return true;
  return error.status >= 500 || error.status === 429 || error.status === 0;
}

/** Keeps a press for later. False when the phone cannot store it (the press shows Not sent instead). */
export function enqueue(entry: QueuedWrite): boolean {
  if (!storageKey()) return false;
  const stored = read();
  if (stored.queue.some(queued => queued.id === entry.id)) return true;
  write({ ...stored, queue: [ ...stored.queue, entry ] });
  startQueue();
  return read().queue.some(queued => queued.id === entry.id);
}

async function deliver(entry: QueuedWrite): Promise<void> {
  switch (entry.kind) {
    case 'tap':
      await fieldDayService.tap(entry.visitId, entry.params);
      return;
    case 'clock_in':
      await fieldDayService.clockIn(entry.params);
      return;
    case 'clock_out':
      await fieldDayService.clockOut(entry.params);
      return;
    case 'break_start':
      await fieldDayService.startBreak();
      return;
    case 'break_end':
      await fieldDayService.endBreak();
  }
}

/** Sends what waits, oldest first, until it is empty or the network fails. */
export async function drainQueue(): Promise<void> {
  if (sending) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  sending = true;
  announce();
  try {
    for (;;) {
      const [ next ] = read().queue;
      if (!next) break;
      try {
        await deliver(next);
        sent += 1;
        const stored = read();
        write({ ...stored, queue: stored.queue.filter(entry => entry.id !== next.id) });
      } catch (error) {
        if (isRetryable(error)) break;
        if (error instanceof BusinessApiError && (error.status === 401 || error.status === 426)) break;
        const stored = read();
        const refused: NotRecorded = {
          id: next.id,
          label: next.label,
          pressedAt: next.pressedAt,
          message: jobRefusalMessage(error, 'The server refused it.'),
        };
        write({
          queue: stored.queue.filter(entry => entry.id !== next.id),
          notRecorded: [ refused, ...stored.notRecorded ].slice(0, NOT_RECORDED_LIMIT),
        });
      }
    }
  } finally {
    sending = false;
    announce();
  }
}

export function dismissNotRecorded(id?: string): void {
  const stored = read();
  write({ ...stored, notRecorded: id ? stored.notRecorded.filter(entry => entry.id !== id) : [] });
}

const onOnline = () => void drainQueue();
const onVisible = () => {
  if (document.visibilityState === 'visible') void drainQueue();
};

/** Idempotent: the field shell starts it; a press that queues starts it too. */
export function startQueue(): void {
  if (!started && typeof window !== 'undefined') {
    started = true;
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    timer = setInterval(() => {
      if (read().queue.length > 0) void drainQueue();
    }, RETRY_EVERY_MS);
  }
  void drainQueue();
}

/** Signing out: nothing of this person's stays on the phone. */
export function clearAllFieldQueues(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(`${PREFIX}:`)) keys.push(key);
    }
    keys.forEach(key => window.localStorage.removeItem(key));
  } catch {
    // Nothing to clear.
  }
  announce();
}

/**
 * Being signed out: keeps each person's queue that still has presses waiting,
 * for their next sign-in, and clears the rest (a queue holding only *Not
 * recorded* notes has nothing left to send).
 */
export function parkFieldQueues(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(`${PREFIX}:`)) keys.push(key);
    }
    keys.forEach(key => {
      if (!hasUnsent(window.localStorage.getItem(key))) window.localStorage.removeItem(key);
    });
  } catch {
    // Nothing to clear.
  }
  announce();
}

function hasUnsent(raw: string | null): boolean {
  try {
    const queue = (JSON.parse(raw ?? 'null') as Stored | null)?.queue;
    return Array.isArray(queue) && queue.length > 0;
  } catch {
    return false;
  }
}

/** For tests. */
export function resetFieldQueue(): void {
  if (timer) clearInterval(timer);
  timer = null;
  if (started && typeof window !== 'undefined') {
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
  }
  started = false;
  sending = false;
  sent = 0;
  listeners.clear();
}
